import React, { useState } from "react";

import logoLight from "assets/images/logo-light.png";
import logoDark from "assets/images/logo-dark.png";
import { Link } from "react-router-dom";
import { Mail, CheckCircle, XCircle } from "lucide-react";
import { getFirebaseBackend } from "../../helpers/firebase_helper";


const ResetPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');

    const firebaseBackend = getFirebaseBackend();

    document.title = "Reset Password | Lyfe Admin Panel";

    React.useEffect(() => {
        const bodyElement = document.body;

        bodyElement.classList.add('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'bg-cover', 'bg-auth-pattern', 'dark:bg-auth-pattern-dark', 'dark:text-zink-100', 'font-public');

        return () => {
            bodyElement.classList.remove('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'bg-cover', 'bg-auth-pattern', 'dark:bg-auth-pattern-dark', 'dark:text-zink-100', 'font-public');
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setShowSuccess(false);
        setLoading(true);

        try {
            await firebaseBackend.forgetPassword(email);

            setShowSuccess(true);
            setEmail('');

            setTimeout(() => {
                setShowSuccess(false);
            }, 10000);

        } catch (err: any) {
            let errorMessage = 'Failed to send reset link. Please try again.';
            
            if (err.includes('auth/user-not-found')) {
                errorMessage = 'No account found with this email address.';
            } else if (err.includes('auth/invalid-email')) {
                errorMessage = 'Invalid email address format.';
            } else if (err.includes('auth/too-many-requests')) {
                errorMessage = 'Too many attempts. Please try again later.';
            } else if (err) {
                errorMessage = err;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="mb-0 border-none lg:w-[500px] card bg-white/70 shadow-none dark:bg-zink-500/70">
                <div className="!px-10 !py-12 card-body">
                    <Link to="/">
                        <img src={logoLight} alt="" className="hidden h-6 mx-auto dark:block" />
                        <img src={logoDark} alt="" className="block h-6 mx-auto dark:hidden" />
                    </Link>

                    <div className="mt-8 text-center">
                        <h4 className="mb-8 text-custom-500 dark:text-custom-500">Forgot Password?</h4>
                    </div>

                    {showSuccess && (
                        <div className="px-4 py-4 mb-6 text-sm border rounded-lg bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                            <div className="flex items-start">
                                <CheckCircle className="flex-shrink-0 w-5 h-5 text-green-500 dark:text-green-400 mt-0.5" />
                                <div className="ml-3 flex-1">
                                    <h5 className="mb-1 text-base font-semibold text-green-800 dark:text-green-200">
                                        Password Reset Link Sent Successfully!
                                    </h5>
                                    <p className="text-green-700 dark:text-green-300">
                                        We've sent a password reset link to your email address. Please check your inbox (and spam folder) and follow the instructions to reset your password. The link will expire in 1 hour.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="px-4 py-4 mb-6 text-sm border rounded-lg bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                            <div className="flex items-start">
                                <XCircle className="flex-shrink-0 w-5 h-5 text-red-500 dark:text-red-400 mt-0.5" />
                                <div className="ml-3 flex-1">
                                    <h5 className="mb-1 text-base font-semibold text-red-800 dark:text-red-200">
                                        Error Sending Reset Link
                                    </h5>
                                    <p className="text-red-700 dark:text-red-300">
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!showSuccess && (
                        <div className="px-4 py-3 mb-6 text-sm text-yellow-500 border border-transparent rounded-md bg-yellow-50 dark:bg-yellow-400/20">
                            Provide your email address, and instructions will be sent to you
                        </div>
                    )}

                    <form autoComplete="off" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="emailInput" className="inline-block mb-2 text-base font-medium">Email</label>
                            <div className="relative">
                                <input 
                                    type="email" 
                                    className="form-input dark:bg-zink-600/50 border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 pl-10" 
                                    required 
                                    placeholder="Enter your email address" 
                                    id="emailInput"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                />
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-zink-300" />
                            </div>
                        </div>
                        <div className="mt-8">
                            <button 
                                type="submit" 
                                className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </span>
                                ) : 'Send Reset Link'}
                            </button>
                        </div>
                        <div className="mt-4 text-center">
                            <p className="mb-0 text-slate-500 dark:text-zink-200">
                                Wait, I remember my password... <Link to="/login" className="underline fw-medium text-custom-500 hover:text-custom-600"> Click here </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </React.Fragment>
    );
}

export default ResetPassword;